"use client";

import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Input } from "@tillix/ui-atoms/input";
import type { LucideIcon } from "lucide-react";
import {
  Building,
  Hash,
  Map as MapIcon,
  MapPin,
  ScanFace,
  UserRound,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  Controller,
  type FieldValues,
  type Path,
  useFormContext,
} from "react-hook-form";
import { useT } from "@/features/i18n/hooks/use-t";

interface AccountFieldProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  labelKey: string;
  placeholderKey: string;
  maxLength?: number;
  inputMode?: "text" | "numeric";
  autoComplete?: string;
  required?: boolean;
  /** Custom onChange filter (e.g. strip non-digits for zip code) */
  sanitize?: (value: string) => string;
  /** Optional icon rendered in InputGroup addon */
  icon?: LucideIcon;
}

function AccountField<TFieldValues extends FieldValues>({
  name,
  labelKey,
  placeholderKey,
  maxLength,
  inputMode,
  autoComplete,
  required,
  sanitize,
  icon: Icon,
}: AccountFieldProps<TFieldValues>) {
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  const { control } = useFormContext<TFieldValues>();
  const label = t(labelKey);
  const placeholder = t(placeholderKey);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldRoot name={field.name}>
          <FieldLabel>
            {label} {required && <span className="text-destructive">*</span>}
          </FieldLabel>

          <FieldControl
            render={(defaultProps) => {
              const inputProps = {
                ...defaultProps,
                ...field,
                autoComplete,
                inputMode,
                maxLength,
                placeholder,
                onChange: sanitize
                  ? (e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(sanitize(e.target.value));
                    }
                  : field.onChange,
              };

              if (Icon) {
                return (
                  <div className="relative">
                    <Icon
                      aria-hidden="true"
                      className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-muted-foreground/72"
                      size={18}
                    />
                    <Input {...inputProps} className="ps-10" />
                  </div>
                );
              }

              return <Input {...inputProps} />;
            }}
          />

          <FieldError>
            {fieldState.error ? tInputs("validation.invalidField") : undefined}
          </FieldError>
        </FieldRoot>
      )}
    />
  );
}

const stripNonDigits = (v: string) => v.replace(/\D/g, "");

export const FIELD_CONFIGS = {
  externalId: {
    labelKey: "accounts.contractAccountNumber",
    placeholderKey: "accounts.contractAccountNumberPlaceholder",
    inputMode: "numeric" as const,
    maxLength: 12,
    icon: Hash,
  },
  nameOnAccount: {
    labelKey: "accounts.nameOnAccountLabel",
    placeholderKey: "accounts.nameOnAccountPlaceholder",
    autoComplete: "name",
    icon: UserRound,
  },
  zipCode: {
    labelKey: "accounts.zipCodeLabel",
    placeholderKey: "accounts.zipCodePlaceholder",
    inputMode: "numeric" as const,
    maxLength: 5,
    autoComplete: "postal-code",
    sanitize: stripNonDigits,
    icon: MapPin,
  },
  nickname: {
    labelKey: "accounts.nicknameLabel",
    placeholderKey: "accounts.nicknamePlaceholder",
    maxLength: 64,
    icon: ScanFace,
  },
  houseNumber: {
    labelKey: "accounts.houseNumberLabel",
    placeholderKey: "accounts.houseNumberPlaceholder",
    maxLength: 10,
    icon: Hash,
  },
  streetName: {
    labelKey: "accounts.streetLabel",
    placeholderKey: "accounts.streetPlaceholder",
    maxLength: 60,
    autoComplete: "address-line1",
    icon: MapPin,
  },
  aptNumber: {
    labelKey: "accounts.aptNumberLabel",
    placeholderKey: "accounts.aptNumberPlaceholder",
    maxLength: 10,
    autoComplete: "address-line2",
    icon: Building,
  },
  state: {
    labelKey: "accounts.stateLabel",
    placeholderKey: "accounts.statePlaceholder",
    maxLength: 3,
    autoComplete: "address-level1",
    icon: MapIcon,
  },
};

export { stripNonDigits };
export type { AccountFieldProps };
export default observer(AccountField);
